import { useState, useEffect, useCallback, useMemo } from 'react'
import { motion } from 'framer-motion'
import { cn } from '@/lib/utils'

interface NavItem {
  id: string
  title: string
}

function extractAllQuestions(survey: any): NavItem[] {
  const items: NavItem[] = []
  const pages = survey?.pages || []

  for (const page of pages) {
    const elements = page.elements || page.questions || []
    for (const el of elements) {
      if (el.type === 'html' || el.type === 'image') continue
      if (el.type === 'panel') {
        const panelEls = el.elements || el.questions || []
        for (const pe of panelEls) {
          if (pe.name && pe.type && pe.type !== 'html' && pe.type !== 'image') {
            items.push({ id: pe.name, title: pe.title || pe.name })
          }
        }
      } else if (el.name && el.type) {
        items.push({ id: el.name, title: el.title || el.name })
      }
    }
  }

  if (items.length === 0 && survey?.questions) {
    for (const q of survey.questions) {
      items.push({ id: q.name, title: q.title || q.name })
    }
  }

  return items
}

/* ─── ProgressDots ─── fixed right-side dot navigation ─── */

interface ProgressDotsProps {
  survey: any
  populationAttributes?: string[]
}

export function ProgressDots({ survey, populationAttributes = [] }: ProgressDotsProps) {
  const items = useMemo(() => extractAllQuestions(survey), [survey])
  const [activeIdx, setActiveIdx] = useState(0)

  useEffect(() => {
    if (items.length === 0) return

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const idx = items.findIndex((item) => item.id === entry.target.id)
            if (idx >= 0) setActiveIdx(idx)
          }
        }
      },
      { rootMargin: '-40% 0px -40% 0px', threshold: 0 },
    )

    for (const item of items) {
      const el = document.getElementById(item.id)
      if (el) observer.observe(el)
    }

    return () => observer.disconnect()
  }, [items])

  const scrollTo = useCallback((id: string) => {
    const el = document.getElementById(id)
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [])

  if (items.length === 0) return null

  return (
    <div className="fixed right-4 top-1/2 -translate-y-1/2 z-50 hidden xl:flex flex-col gap-4">
      {items.map((item, idx) => (
        <button
          key={item.id}
          onClick={() => scrollTo(item.id)}
          className={cn(
            'group relative w-2 h-2 rounded-full transition-all duration-300',
            activeIdx === idx ? 'bg-foreground scale-[1.3]' : 'bg-border hover:bg-muted-foreground hover:scale-[1.3]',
          )}
          aria-label={item.title}
        >
          <span className="pointer-events-none absolute right-5 top-1/2 -translate-y-1/2 whitespace-nowrap text-[13px] text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity bg-background px-2 py-1 rounded shadow-sm">
            {item.title}
          </span>
        </button>
      ))}
    </div>
  )
}

/* ─── ContentsOverlay ─── full-screen table of contents ─── */

interface ContentsOverlayProps {
  survey: any
  populationAttributes?: string[]
  onClose: () => void
}

export function ContentsOverlay({ survey, onClose }: ContentsOverlayProps) {
  const items = useMemo(() => extractAllQuestions(survey), [survey])

  const scrollTo = useCallback(
    (id: string) => {
      onClose()
      setTimeout(() => {
        const el = document.getElementById(id)
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 300)
    },
    [onClose],
  )

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopImmediatePropagation()
        onClose()
      }
    }
    window.addEventListener('keydown', handleEsc, true)
    return () => window.removeEventListener('keydown', handleEsc, true)
  }, [onClose])

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="fixed inset-0 z-[1000] bg-background/[0.98] backdrop-blur-xl flex"
    >
      <button
        onClick={onClose}
        className="absolute top-8 right-8 w-10 h-10 bg-muted rounded-full flex items-center justify-center text-foreground text-xl hover:bg-muted-foreground/20 transition-colors"
      >
        &times;
      </button>

      <div className="m-auto max-w-[600px] w-full px-10">
        <div className="text-center mb-12">
          <h2 className="text-[32px] font-semibold tracking-tight mb-3">Contents</h2>
          <p className="text-[15px] text-muted-foreground">{items.length} questions</p>
        </div>

        <ul className="list-none">
          {items.map((item, idx) => (
            <li key={item.id}>
              <button
                onClick={() => scrollTo(item.id)}
                className="flex items-center gap-4 w-full py-4 border-b border-muted text-left transition-all hover:pl-2"
              >
                <span className="w-8 h-8 bg-primary text-primary-foreground rounded-lg flex items-center justify-center text-[15px] font-semibold shrink-0">
                  {idx + 1}
                </span>
                <span className="text-[17px] font-medium text-foreground truncate">
                  {item.title}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </motion.div>
  )
}
